import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  addWarehouseToState,
  createStateAndWarehouse,
  getAllStates,
  getStateById,
  updateWarehouseStore,
} from "../thunk";
import { State } from "../../types/types";
import toast from "react-hot-toast";

interface WarehouseState {
  allStates: State[];
  allStatesStatus: "idle" | "loading" | "succeeded" | "failed";
  allStatesError: string | null;
  selectedState: State | null;
  selectedStateStatus: "idle" | "loading" | "succeeded" | "failed";
  selectedStateError: string | null;
  updateStatus?: "idle" | "loading" | "succeeded" | "failed";
  updateError?: string | null;
}

const initialState: WarehouseState = {
  allStates: [],
  allStatesStatus: "idle",
  allStatesError: null,
  selectedState: null,
  selectedStateStatus: "idle",
  selectedStateError: null,
  updateStatus: "idle",
  updateError: null,
};

const warehouseSlice = createSlice({
  name: "warehouse",
  initialState,
  reducers: {
    clearSelectedState: (state) => {
      state.selectedState = null;
      state.selectedStateStatus = "idle";
      state.selectedStateError = null;
      state.updateStatus = "idle";
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllStates.pending, (state) => {
        state.allStatesStatus = "loading";
        state.allStatesError = null;
      })
      .addCase(
        getAllStates.fulfilled,
        (state, action: PayloadAction<State[]>) => {
          state.allStatesStatus = "succeeded";
          state.allStates = action.payload;
        }
      )
      .addCase(getAllStates.rejected, (state, action) => {
        state.allStatesStatus = "failed";
        state.allStatesError =
          (action.payload as string) || "Unknown error fetching states";
      })
      .addCase(getStateById.pending, (state) => {
        state.selectedStateStatus = "loading";
        state.selectedStateError = null;
      })
      .addCase(
        getStateById.fulfilled,
        (state, action: PayloadAction<State>) => {
          state.selectedStateStatus = "succeeded";
          state.selectedState = action.payload;
        }
      )
      .addCase(getStateById.rejected, (state, action) => {
        state.selectedStateStatus = "failed";
        state.selectedStateError =
          (action.payload as string) || "Unknown error fetching state details";
        state.selectedState = null;
      })
      .addCase(updateWarehouseStore.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(
        updateWarehouseStore.fulfilled,
        (state, action: PayloadAction<State>) => {
          state.updateStatus = "succeeded";
          if (state.selectedState?._id === action.payload._id) {
            state.selectedState = action.payload;
          }
          const indexInAllStates = state.allStates.findIndex(
            (s) => s._id === action.payload._id
          );
          if (indexInAllStates !== -1) {
            state.allStates[indexInAllStates] = action.payload;
          }
        }
      )
      .addCase(updateWarehouseStore.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = (action.payload as string) || "Update failed";
      })
      .addCase(createStateAndWarehouse.pending, (state) => {
        state.allStatesStatus = "loading";
        state.allStatesError = null;
      })
      .addCase(
        createStateAndWarehouse.fulfilled,
        (state, action: PayloadAction<State>) => {
          state.allStatesStatus = "idle";
        }
      )
      .addCase(createStateAndWarehouse.rejected, (state, action) => {
        state.allStatesStatus = "failed";
        state.allStatesError =
          (action.payload as string) || "Failed to create state";
        toast.error(state.allStatesError);
      })
      .addCase(addWarehouseToState.pending, (state) => {
        state.selectedStateStatus = "loading";
      })
      .addCase(
        addWarehouseToState.fulfilled,
        (state, action: PayloadAction<State>) => {
          state.selectedStateStatus = "succeeded";
          if (state.selectedState?._id === action.payload._id) {
            state.selectedState = action.payload;
          }
          const indexInAllStates = state.allStates.findIndex(
            (s) => s._id === action.payload._id
          );
          if (indexInAllStates !== -1) {
            state.allStates[indexInAllStates] = action.payload;
          }
        }
      )
      .addCase(addWarehouseToState.rejected, (state, action) => {
        state.selectedStateStatus = "failed";
        state.selectedStateError =
          (action.payload as string) || "Failed to add warehouse";
        toast.error(state.selectedStateError);
      });
  },
});

export const { clearSelectedState } = warehouseSlice.actions;
export default warehouseSlice.reducer;
